import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, MessageSquareQuote } from "lucide-react";
import { API, IMPIANTI } from "@/lib/config";
import { Stelle } from "@/components/Stelle";

const dataBreve = (iso) => new Date(iso).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

const Card = ({ r }) => (
  <article className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm flex flex-col gap-4" data-testid={`recensione-card-${r.id}`}>
    <div className="flex items-center justify-between gap-2">
      <Stelle voto={r.voto} />
      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5" data-testid="recensione-verificata-badge">
        <BadgeCheck className="w-3.5 h-3.5" /> Cliente verificato
      </span>
    </div>
    <p className="text-slate-700 leading-relaxed flex-1">“{r.testo}”</p>
    <div className="text-sm border-t border-slate-100 pt-3">
      <p className="font-bold">{r.nome_visibile}</p>
      <p className="text-slate-500">{r.tipo_problema} · {IMPIANTI[r.impianto]} · {dataBreve(r.data_intervento)}</p>
    </div>
  </article>
);

export const Recensioni = () => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/recensioni`).then((r) => setData(r.data)).catch(() => setData({ totale: 0, recensioni: [] })); }, []);

  return (
    <section id="recensioni" className="bg-sky-50/60 border-y border-sky-100" data-testid="recensioni-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-wider text-sky-700">Recensioni verificate</p>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Cosa dicono i nostri clienti</h2>
            <p className="mt-3 text-slate-600">Ogni recensione è collegata a un intervento realmente eseguito: solo chi ha ricevuto un nostro intervento riceve il link personale per recensirlo.</p>
          </div>
          {data?.totale > 0 && (
            <div className="rounded-2xl bg-white border border-slate-200 px-6 py-4 flex items-center gap-4" data-testid="recensioni-media">
              <span className="text-4xl font-extrabold">{data.media.toString().replace(".", ",")}</span>
              <div><Stelle voto={Math.round(data.media)} size="w-5 h-5" /><p className="text-sm text-slate-500 mt-1">{data.totale} recensioni verificate</p></div>
            </div>
          )}
        </div>
        {data && data.totale === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-sky-300 bg-white p-10 text-center" data-testid="recensioni-empty">
            <MessageSquareQuote className="w-10 h-10 text-sky-500 mx-auto" />
            <p className="mt-3 font-semibold">Le prime recensioni verificate arriveranno presto.</p>
            <p className="text-sm text-slate-500 mt-1">Al termine di ogni intervento il cliente riceve un link per lasciare la sua opinione.</p>
          </div>
        )}
        {data?.totale > 0 && (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="recensioni-list">
            {data.recensioni.map((r) => <Card key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </section>
  );
};
